// Contact Form Modal JavaScript
class ContactFormModal {
  constructor() {
    this.modal = null;
    this.openTriggers = [];
    this.isOpen = false;
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
    
    this.init();
  }

  init() {
    // Find modal element
    this.modal = document.querySelector('.contact-form-modal');
    if (!this.modal) {
      return;
    }

    // Find all trigger buttons
    this.openTriggers = document.querySelectorAll('[data-modal-trigger="contact-form"]');
    
    // Bind events
    this.bindEvents();
    
    // Set up focusable elements
    this.setupFocusableElements();
  }

  bindEvents() {
    // Open modal triggers
    this.openTriggers.forEach((trigger, index) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.openModal();
      });
    });

    // Close modal triggers
    const closeBtn = this.modal.querySelector('.modal-close-btn');
    const backdrop = this.modal.querySelector('.modal-backdrop');
    
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeModal();
      });
    }

    if (backdrop) {
      backdrop.addEventListener('click', () => {
        this.closeModal();
      });
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeModal();
      }
    });

    // Tab trap for accessibility
    this.modal.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        this.handleTabKey(e);
      }
    });
  }

  setupFocusableElements() {
    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'textarea:not([disabled])',
      'select:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])'
    ];

    this.focusableElements = this.modal.querySelectorAll(focusableSelectors.join(', '));
    this.firstFocusableElement = this.focusableElements[0];
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
  }

  openModal() {
    if (this.isOpen) return;

    // Prevent body scroll
    document.body.classList.add('modal-open');
    
    // Show modal
    this.modal.classList.add('modal-open');
    this.modal.setAttribute('aria-hidden', 'false');
    
    // Focus management
    this.modal.focus();
    if (this.firstFocusableElement) {
      setTimeout(() => {
        this.firstFocusableElement.focus();
      }, 100);
    }
    
    this.isOpen = true;
    
    // Trigger custom event
    this.modal.dispatchEvent(new CustomEvent('modalOpened', {
      detail: { modalType: 'contact-form' }
    }));
  }

  closeModal() {
    if (!this.isOpen) return;

    // Hide modal
    this.modal.classList.remove('modal-open');
    this.modal.setAttribute('aria-hidden', 'true');
    
    // Allow body scroll
    document.body.classList.remove('modal-open');
    
    // Return focus to trigger element
    const activeTrigger = document.querySelector('[data-modal-trigger="contact-form"]:focus');
    if (activeTrigger) {
      activeTrigger.focus();
    } else if (this.openTriggers[0]) {
      this.openTriggers[0].focus();
    }
    
    this.isOpen = false;
    
    // Clear form if needed
    this.clearForm();
    
    // Trigger custom event
    this.modal.dispatchEvent(new CustomEvent('modalClosed', {
      detail: { modalType: 'contact-form' }
    }));
  }

  handleTabKey(e) {
    if (!this.firstFocusableElement || !this.lastFocusableElement) return;

    if (e.shiftKey) {
      // Shift + Tab
      if (document.activeElement === this.firstFocusableElement) {
        e.preventDefault();
        this.lastFocusableElement.focus();
      }
    } else {
      // Tab
      if (document.activeElement === this.lastFocusableElement) {
        e.preventDefault();
        this.firstFocusableElement.focus();
      }
    }
  }

  clearForm() {
    const form = this.modal.querySelector('form');
    if (form) {
      // Reset form fields
      form.reset();
      
      // Clear any error states
      const errorElements = form.querySelectorAll('.form-error');
      errorElements.forEach(error => error.remove());
      
      // Remove invalid classes
      const invalidInputs = form.querySelectorAll('.form-input--invalid');
      invalidInputs.forEach(input => input.classList.remove('form-input--invalid'));
    }
  }

  // Public API methods
  open() {
    this.openModal();
  }

  close() {
    this.closeModal();
  }

  toggle() {
    if (this.isOpen) {
      this.closeModal();
    } else {
      this.openModal();
    }
  }
}

// Form validation enhancement for modal
class ContactFormModalValidator {
  constructor(modal) {
    this.modal = modal;
    this.form = this.modal.querySelector('form');
    this.submitBtn = this.form?.querySelector('.form-submit-btn');
    
    // Only initialize if this is actually a modal form, not a regular contact form section
    if (this.form && this.modal.classList.contains('contact-form-modal')) {
      this.init();
    }
  }

  init() {
    // Real-time validation
    const inputs = this.form.querySelectorAll('.form-input, .form-textarea');
    inputs.forEach(input => {
      input.addEventListener('blur', () => this.validateField(input));
      input.addEventListener('input', () => this.clearFieldError(input));
    });

    // Form submission
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  validateField(field) {
    const value = field.value.trim();
    const fieldName = field.getAttribute('name');
    let isValid = true;
    let errorMessage = '';

    // Clear previous errors
    this.clearFieldError(field);

    // Required field validation
    if (field.hasAttribute('required') && !value) {
      isValid = false;
      errorMessage = 'This field is required.';
    }

    // Email validation
    if (fieldName === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        isValid = false;
        errorMessage = 'Please enter a valid email address.';
      }
    }

    // Phone validation
    if (fieldName === 'phone' && value) {
      const phoneRegex = /^[\+]?[\d\s\-\(\)]{10,}$/;
      if (!phoneRegex.test(value)) {
        isValid = false;
        errorMessage = 'Please enter a valid phone number.';
      }
    }

    if (!isValid) {
      this.showFieldError(field, errorMessage);
    }

    return isValid;
  }

  showFieldError(field, message) {
    field.classList.add('form-input--invalid');
    
    // Create error element
    const errorElement = document.createElement('div');
    errorElement.className = 'form-error';
    errorElement.textContent = message;
    errorElement.setAttribute('role', 'alert');
    
    // Insert after field
    field.parentNode.insertBefore(errorElement, field.nextSibling);
  }

  clearFieldError(field) {
    field.classList.remove('form-input--invalid');
    
    // Remove error message
    const errorElement = field.parentNode.querySelector('.form-error');
    if (errorElement) {
      errorElement.remove();
    }
  }

  validateForm() {
    const inputs = this.form.querySelectorAll('.form-input, .form-textarea');
    let isFormValid = true;

    inputs.forEach(input => {
      if (!this.validateField(input)) {
        isFormValid = false;
      }
    });

    return isFormValid;
  }

  handleSubmit(e) {
    // Only prevent default and handle custom validation for modal forms
    if (!this.modal.classList.contains('contact-form-modal')) {
      // Let Shopify handle regular contact forms naturally
      return true;
    }
    
    e.preventDefault();
    
    if (this.validateForm()) {
      // Show loading state
      this.submitBtn.textContent = 'Sending...';
      this.submitBtn.disabled = true;
      
      // For modal forms, we can submit via AJAX to avoid page redirect
      const formData = new FormData(this.form);
      
      fetch(this.form.action, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      }).then(response => {
        if (response.ok) {
          this.showSuccessMessage();
        } else {
          throw new Error('Form submission failed');
        }
      }).catch(error => {
        console.error('Form submission error:', error);
        // Fallback to regular form submission
        this.form.submit();
      }).finally(() => {
        this.submitBtn.textContent = 'Send Message';
        this.submitBtn.disabled = false;
      });
    }
  }

  showSuccessMessage() {
    const successElement = document.createElement('div');
    successElement.className = 'form-success';
    successElement.innerHTML = `
      <div class="success-icon">✓</div>
      <h3>Message Sent Successfully!</h3>
      <p>Thank you for your inquiry. We'll get back to you within 24 hours.</p>
    `;
    successElement.setAttribute('role', 'alert');
    
    // Replace form with success message
    this.form.style.display = 'none';
    this.form.parentNode.appendChild(successElement);
    
    // Auto-close modal after 3 seconds
    setTimeout(() => {
      window.contactFormModal.close();
      
      // Reset form display for next time
      setTimeout(() => {
        this.form.style.display = 'block';
        successElement.remove();
        this.form.reset();
      }, 500);
    }, 3000);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Initialize modal
  window.contactFormModal = new ContactFormModal();
  
  // Initialize validator ONLY for modal forms, not regular contact form sections
  const modal = document.querySelector('.contact-form-modal');
  if (modal) {
    window.contactFormValidator = new ContactFormModalValidator(modal);
  }
});

// Shopify theme compatibility
if (typeof Shopify !== 'undefined' && Shopify.theme) {
  // Register with Shopify theme events
  document.addEventListener('shopify:theme:loaded', function() {
    if (window.contactFormModal) {
      window.contactFormModal.init();
    }
  });
}

// Additional fallback initialization for Shopify themes
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initModal);
} else {
  // DOM already loaded
  initModal();
}

function initModal() {
  if (!window.contactFormModal) {
    window.contactFormModal = new ContactFormModal();
    
    const modal = document.querySelector('.contact-form-modal');
    if (modal && !window.contactFormValidator) {
      window.contactFormValidator = new ContactFormModalValidator(modal);
    }
  }
}